import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAIDashboardStore } from "../context/AIDashboardContext";

/**
 * Custom hook for coordinating maximum Y-axis values across multiple chart instances.
 *
 * This hook allows multiple charts in a dashboard to share the same Y-axis scale by
 * publishing their local maximum values to a shared channel and consuming the highest
 * published value.
 *
 * @param {string} channelName - The channel name for coordinating max values
 * @param {Function} calculateLocalMax - Callback function that calculates the local max value from chart data
 * @returns {{ coordinatedMax: number, resetMax: Function }} - The coordinated max value and reset function
 *
 * @example
 * const calculateMax = useCallback(() => {
 *   return Math.max(...chartDataValues);
 * }, [chartDataValues]);
 *
 * const { coordinatedMax, resetMax } = useCoordinatedChartMax(
 *   `${dashboardChannel}-max-forecast-display-value`,
 *   calculateMax
 * );
 */
const useCoordinatedChartMax = (channelName, calculateLocalMax) => {
  const maxStore = useAIDashboardStore(channelName);
  const publishedMax = maxStore((state) => state.value);
  const publish = maxStore((state) => state.publish);
  const isReceivingUpdate = useRef(false);
  const hasInitialized = useRef(false);

  // Calculate the local max Y value from the data
  const localMaxY = useMemo(() => {
    return calculateLocalMax();
  }, [calculateLocalMax]);

  // Initial reset on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      publish(0);
      hasInitialized.current = true;
    }
  }, [publish]);

  // Publish the max value if it's higher than the published max
  useEffect(() => {
    // If publishedMax is null (after reset), always allow publishing
    // Otherwise, skip if we're receiving an update
    if (isReceivingUpdate.current && publishedMax != null) {
      // Don't publish if we're currently receiving an update from another chart
      return;
    }

    if (localMaxY > 0 && (publishedMax == null || localMaxY > publishedMax)) {
      publish(localMaxY);
    }
  }, [localMaxY, publishedMax, publish]);

  // Subscribe to changes from other charts and update our max
  const coordinatedMax = useMemo(() => {
    if (publishedMax != null && publishedMax > localMaxY) {
      isReceivingUpdate.current = true;
      // Use the published max if it's higher than our local max
      return publishedMax;
    }
    isReceivingUpdate.current = false;
    return localMaxY;
  }, [publishedMax, localMaxY]);

  // Provide a reset function for parent components
  const resetMax = useCallback(
    (value) => {
      publish(value);
      if (value === null) {
        hasInitialized.current = false;
      }
    },
    [publish]
  );

  return { coordinatedMax, resetMax };
};

export default useCoordinatedChartMax;
